import React, { useRef, useEffect } from "react";
import {
  animateTitle,
  animateTimelineLine,
  animateTimelineEvents,
  cleanupScrollTriggers,
} from "./gsap";

interface TimelineEvent {
  id: string;
  year: string;
  title: string;
  description: string;
}

// Timeline events data
const timelineEvents: TimelineEvent[] = [
  {
    id: "founding",
    year: "2019",
    title: "Street Lawyer Services Founded",
    description:
      "Lonny Bramzon establishes Street Lawyer Services in Washington DC, bringing premium cannabis products to the DMV area.",
  },
  {
    id: "event-series",
    year: "2020",
    title: "Community Event Series",
    description:
      "Launched weekly community events, bringing together cannabis enthusiasts and advocates across the DMV.",
  },
  {
    id: "covid-resilience",
    year: "2020",
    title: "Adapted Through Pandemic",
    description:
      "SLS remained operational during COVID-19, implementing safety measures while continuing to serve the DC community.",
  },
  {
    id: "dc-license",
    year: "2025",
    title: "Washington DC License Obtained",
    description:
      "Secured official dispensary licensing in Washington DC, allowing us to expand our operations and serve the community as a fully licensed cannabis retailer.",
  },
];

const TimelineSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const timelineLineRef = useRef<HTMLDivElement>(null);
  const eventRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    // Apply animations with a small timeout to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (sectionRef.current && titleRef.current && timelineLineRef.current) {
        // Animate section title
        animateTitle(
          { current: titleRef.current },
          { current: sectionRef.current }
        );

        // Animate timeline line
        animateTimelineLine(
          { current: timelineLineRef.current },
          { current: sectionRef.current }
        );

        // Animate timeline events
        const eventElements = eventRefs.current.map((ref) => ({
          current: ref,
        }));
        animateTimelineEvents(eventElements, { current: sectionRef.current });
      }
    }, 100);

    // Cleanup animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <div ref={sectionRef} className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-3xl md:text-4xl font-bold mb-12 text-center"
        >
          Our Journey
        </h2>

        <div className="relative max-w-4xl mx-auto">
          {/* Timeline vertical line */}
          <div
            ref={timelineLineRef}
            className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-cannabis-green-400"
            style={{ top: 0 }}
          ></div>

          {/* Timeline events */}
          <div className="relative">
            {timelineEvents.map((event, index) => (
              <div
                key={event.id}
                ref={(el) => (eventRefs.current[index] = el)}
                className={`relative mb-16 flex items-center ${
                  index % 2 === 0 ? "justify-start" : "justify-end"
                }`}
              >
                {/* Timeline dot */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-5 h-5 rounded-full bg-cannabis-green-500 border-4 border-white z-10"></div>

                {/* Event content */}
                <div
                  className={`w-5/12 bg-gray-50 p-6 rounded-lg shadow-md ${
                    index % 2 === 0 ? "text-right mr-12" : "ml-12"
                  }`}
                >
                  <span className="block text-cannabis-green-500 font-bold text-xl mb-1">
                    {event.year}
                  </span>
                  <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
                  <p className="text-gray-700">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineSection;
